// Global Instructions Rule Applied!

/**
 * InputManagerField Component
 *
 * Individual field component for the InputManager system.
 * Handles display/edit mode switching and renders appropriate input based on field type.
 */

import React, { useEffect, useRef, useCallback, useState } from 'react'
import { Typography, Button, Popconfirm } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FieldTypeEnum, InputManagerFieldProps, type BaseFieldConfig } from './types.ts'
import { useFieldEditing } from './hooks/useInputManager.ts'
import { useInputManagerField } from './hooks/useInputManagerField.ts'
import { getDisplayValue } from './utils/displayValue.tsx'
import { useLookups } from '../../../app_modules/global_configs/hooks/useLookups.ts'
import { useTabManagerContext } from '../../app-layout/lib/TabManagerContext.tsx'
import { useSelector } from 'react-redux'
import {
  TextInput,
  NumberInput,
  SelectInput,
  TextareaInput,
  ContactInput,
  ValuationInput,
  RichTextInput,
  DateInput,
  TimeInput,
  SwitchInput,
  CheckboxInput,
  CompanySelect,
  CountrySelect,
  CitySelect,
  DealSelect,
  ProspectSelect,
  TagsInput
} from './components/index.ts'
import {
  isTextFieldConfig,
  isNumberFieldConfig,
  isSelectFieldConfig,
  isTextareaFieldConfig,
  isCompanyFieldConfig,
  isCityFieldConfig,
  isContactFieldConfig,
  isValuationFieldConfig,
  isRichTextFieldConfig,
  isDateFieldConfig,
  isTimeFieldConfig,
  isSwitchFieldConfig,
  isCheckboxFieldConfig,
  isDealFieldConfig,
  isProspectFieldConfig,
  isTagsFieldConfig
} from './utils/typeGuards.ts'

const { Text } = Typography

// ============================================================================
// Component Implementation
// ============================================================================

/**
 * InputManagerField Component
 * Renders individual fields with inline editing capabilities
 */
export const InputManagerField: React.FC<InputManagerFieldProps> = ({
  config,
  isEditing,
  isSaving,
  tempValue,
  validationError,
  onStartEdit,
  onConfirm,
  onCancel,
  onValueChange,
  onBlur,
  hideActions = false,
  alignment = 'left',
  onEditModeChange,
  onClick,
  record,
  onFieldEdit,
  textClassName,
  inputMode = 'read',
  labelPosition
}) => {
  const inputRef = useRef<any>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { updateTempValue, resetField } = useFieldEditing(config.key)
  const { getLookupOptions } = useLookups()
  const { openFormInTab } = useTabManagerContext()
  const countriesData = useSelector((state: any) => state.globalConfigs?.countries || [])

  const [showPopconfirm, setShowPopconfirm] = useState(false)

  const hasChanges = useCallback(() => {
    return tempValue !== config.value
  }, [tempValue, config.value])

  const {
    handleStartEdit,
    handleConfirm,
    handleCancel,
    handleValueChange,
    handleKeyPress,
    handleBlurInternal,
    handlePopconfirmConfirm,
    handlePopconfirmCancel
  } = useInputManagerField({
    config,
    inputMode,
    tempValue,
    onStartEdit,
    onConfirm,
    onCancel,
    onValueChange,
    onBlur,
    onFieldEdit,
    updateTempValue,
    resetField,
    setShowPopconfirm,
    hasChanges
  })

  // Update temp value when prop changes
  useEffect(() => {
    if (tempValue !== undefined) {
      updateTempValue(tempValue, config.value)
    }
  }, [tempValue, config.value, updateTempValue])

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current && config.type !== FieldTypeEnum.RICHTEXT) {
      // In 'edit' mode (all fields editable), only focus if autoFocus is explicitly true
      // In 'read' mode (click to edit), always focus the clicked field
      const shouldFocus = inputMode === 'read' || config.autoFocus === true

      if (shouldFocus) {
        setTimeout(() => {
          // Check if user is already typing in this field - don't interrupt
          const activeElement = document.activeElement
          if (activeElement && containerRef.current && containerRef.current.contains(activeElement)) {
            // User is already typing, don't re-focus
            return
          }

          // Additional check: if any input in the document has focus and user is typing, don't focus
          // This prevents focus shifts when user is actively typing in any field
          if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
            // User is typing somewhere, don't interrupt
            return
          }

          // Try direct focus first
          if (inputRef.current?.focus) {
            try {
              inputRef.current.focus()
              return
            } catch (e) {
              // Fall through to querySelector approach
            }
          }

          // Try to find and focus nested input elements for complex components
          if (inputRef.current?.querySelector) {
            // Try various selectors for different Ant Design components
            const selectors = [
              'input',
              'textarea',
              '.ant-select-selector',
              '.ant-picker-input input',
              '.ant-time-picker-input',
              '.ant-input',
              '[role="combobox"]',
              '[role="textbox"]'
            ]

            for (const selector of selectors) {
              const element = inputRef.current.querySelector(selector) as HTMLElement
              if (element && typeof element.focus === 'function') {
                try {
                  element.focus()
                  return
                } catch (e) {
                  // Continue to next selector
                }
              }
            }
          }
        }, 100)
      }
    }
  }, [isEditing, config.type, config.autoFocus, inputMode])

  // Notify parent when edit mode changes
  useEffect(() => {
    if (onEditModeChange) {
      onEditModeChange(isEditing)
    }
  }, [isEditing, onEditModeChange])

  // Handle clicks outside the component. The document listener closes over
  // the render it was attached in, so the blur policy is read through a ref —
  // by the time the deferred call runs, blur-driven onChange commits (Jodit,
  // InputNumber formatting, half-typed DatePicker text) have already landed
  // in tempValue.
  const blurPolicyRef = useRef(handleBlurInternal)
  blurPolicyRef.current = handleBlurInternal
  useEffect(() => {
    if (!isEditing) return

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Check if the click is on a Select dropdown option or other Ant Design overlay
        // (same portal set RoundSecuritiesCard's click-away save whitelists).
        const isAntOverlay = (event.target as Element)?.closest?.(
          '.ant-select-dropdown, .ant-select-item, .ant-popover, .ant-tooltip, .ant-popconfirm, .ant-picker-dropdown, .ant-cascader-dropdown, .ant-dropdown, .ant-modal-root, .ant-message'
        )

        if (!isAntOverlay) {
          // mousedown fires BEFORE the focused input blurs — blur it and let
          // React flush that state before the save-on-click-away policy reads
          // the draft, or the value still being typed would be lost.
          //
          // Only ever blur THIS field's own input. In `edit` mode every field
          // is permanently "editing", so every one of them has this listener
          // attached and treats the whole rest of the page as outside. An
          // unguarded blur therefore fired once per edit-mode field on every
          // click anywhere — including the click that opens a Select
          // elsewhere on the page, whose freshly focused search input was
          // blurred a few ms later and took the open dropdown down with it.
          const active = document.activeElement as HTMLElement | null
          if (active && containerRef.current.contains(active)) {
            active.blur?.()
          }
          setTimeout(() => blurPolicyRef.current(), 0)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isEditing])

  // Determine if field is readonly (either editable === false or disabled === true)
  const isReadonly = config.editable === false || config.disabled === true

  const displayValue = getDisplayValue(config, {
    textClassName,
    getLookupOptions,
    labelPosition,
    onHyperlinkClick: openFormInTab,
    countriesData,
    readonly: isReadonly,
    alignment
  })

  // ============================================================================
  // Input Renderers
  // ============================================================================

  const renderInput = () => {
    switch (config.type) {
      case FieldTypeEnum.TEXT:
      case FieldTypeEnum.TEXT_ACTION:
      case FieldTypeEnum.LINK:
      case FieldTypeEnum.URL:
      case FieldTypeEnum.EMAIL:
      case FieldTypeEnum.PHONE:
        if (isTextFieldConfig(config)) {
          return (
            <TextInput
              ref={inputRef}
              value={tempValue}
              onChange={handleValueChange}
              onKeyDown={handleKeyPress}
              size={config.size || 'small'}
              status={validationError ? 'error' : undefined}
              placeholder={config.placeholder}
              maxLength={config.maxLength}
              minLength={config.minLength}
              pattern={config.pattern}
              autoFocus={inputMode === 'read' ? true : config.autoFocus === true}
            />
          )
        }
        break

      case FieldTypeEnum.NUMBER:
      case FieldTypeEnum.CURRENCY:
        if (isNumberFieldConfig(config)) {
          return (
            <NumberInput
              ref={inputRef}
              value={tempValue}
              onChange={handleValueChange}
              onKeyDown={handleKeyPress}
              size={config.size || 'small'}
              status={validationError ? 'error' : undefined}
              placeholder={config.placeholder}
              min={config.min}
              max={config.max}
              step={config.step}
              formatter={config.formatter}
              parser={config.parser}
              currencySymbol={config.currencySymbol}
              fieldType={config.type}
              autoFocus={inputMode === 'read' ? true : config.autoFocus === true}
            />
          )
        }
        break

      case FieldTypeEnum.SELECT:
        if (isSelectFieldConfig(config)) {
          return (
            <SelectInput
              ref={inputRef}
              value={tempValue}
              onChange={handleValueChange}
              onKeyDown={handleKeyPress}
              size={config.size || 'small'}
              status={validationError ? 'error' : undefined}
              placeholder={config.placeholder}
              options={config.options}
              selectType={config.selectType}
              lookupCategory={config.lookupCategory}
              showSearch={config.showSearch}
              allowClear={config.allowClear}
              disabled={config.disabled}
              autoFocus={inputMode === 'read' ? true : config.autoFocus === true}
              mode={config.mode}
              optionsFooter={config.optionsFooter}
              optionOrder={config.optionOrder}
            />
          )
        }
        break

      case FieldTypeEnum.TEXTAREA:
        if (isTextareaFieldConfig(config)) {
          return (
            <TextareaInput
              ref={inputRef}
              value={tempValue}
              onChange={handleValueChange}
              onKeyDown={handleKeyPress}
              size={config.size || 'small'}
              status={validationError ? 'error' : undefined}
              placeholder={config.placeholder}
              rows={config.rows}
              maxLength={config.maxLength}
              autoSize={config.autoSize}
              autoFocus={inputMode === 'read' ? true : config.autoFocus === true}
            />
          )
        }
        break

      case FieldTypeEnum.COMPANY:
        if (isCompanyFieldConfig(config)) {
          return (
            <CompanySelect
              ref={inputRef}
              mode='inline'
              selectMode={config.mode || 'single'}
              value={tempValue}
              onChange={(selectedValue, option) => handleValueChange(option || selectedValue)}
              onSelect={(selectedValue, option) => handleValueChange(option || selectedValue)}
              onClear={() => handleValueChange(config.mode === 'multiple' ? [] : '')}
              contextKey={`field-${config.key}`}
              getCompanyDetailsById={config.getCompanyDetailsById}
              onCompanyDetailsUpdate={config.onCompanyDetailsUpdate}
              size={config.size || 'small'}
              style={{ width: '100%' }}
              autoFocus={inputMode === 'read' ? true : config.autoFocus === true}
              placeholder={config.placeholder}
              onKeyDown={handleKeyPress}
              disabled={config.disabled}
              error={!!validationError}
              required={config.required}
            />
          )
        }
        break

      case FieldTypeEnum.COUNTRY:
        return (
          <CountrySelect
            ref={inputRef}
            mode='inline'
            value={tempValue}
            onChange={(selectedValue, option) => handleValueChange(option || selectedValue)}
            onSelect={(selectedValue, option) => handleValueChange(option || selectedValue)}
            onClear={() => handleValueChange('')}
            contextKey={`field-${config.key}`}
            size={config.size || 'small'}
            style={{ width: '100%' }}
            autoFocus={inputMode === 'read' ? true : config.autoFocus === true}
            placeholder={config.placeholder}
            onKeyDown={handleKeyPress}
            disabled={config.disabled}
          />
        )

      case FieldTypeEnum.CITY:
        if (isCityFieldConfig(config)) {
          return (
            <CitySelect
              key={`city-${config.key}-${config.country || 'no-country'}`}
              ref={inputRef}
              mode='inline'
              value={tempValue}
              onChange={(selectedValue, option) => handleValueChange(option || selectedValue)}
              onSelect={(selectedValue, option) => handleValueChange(option || selectedValue)}
              onClear={() => handleValueChange('')}
              contextKey={`field-${config.key}`}
              country={config.country}
              size={config.size || 'small'}
              style={{ width: '100%' }}
              autoFocus={inputMode === 'read' ? true : config.autoFocus === true}
              placeholder={config.placeholder}
              onKeyDown={handleKeyPress}
              disabled={config.disabled}
            />
          )
        }
        break

      case FieldTypeEnum.CONTACT:
        if (isContactFieldConfig(config)) {
          return (
            <ContactInput
              ref={inputRef}
              value={tempValue}
              onChange={handleValueChange}
              onKeyDown={handleKeyPress}
              size={config.size || 'small'}
              placeholder={config.placeholder}
              mode={config.mode}
              options={config.options}
              companyId={config.companyId}
              allowAddNew={config.allowAddNew !== undefined ? config.allowAddNew : true}
              disabled={config.disabled}
              autoFocus={inputMode === 'read' ? true : config.autoFocus === true}
            />
          )
        }
        break

      case FieldTypeEnum.VALUATION:
        if (isValuationFieldConfig(config)) {
          return (
            <ValuationInput
              ref={inputRef}
              value={tempValue}
              onChange={handleValueChange}
              onKeyDown={handleKeyPress}
              currencySymbol={config.currencySymbol}
              min={config.min}
              max={config.max}
              step={config.step}
            />
          )
        }
        break

      case FieldTypeEnum.RICHTEXT:
        if (isRichTextFieldConfig(config)) {
          return (
            <RichTextInput
              ref={inputRef}
              value={tempValue}
              onChange={handleValueChange}
              onLiveChange={config.onLiveChange}
              placeholder={config.placeholder}
              editorConfig={config.editorConfig}
              size={config.size || 'small'}
              onKeyDown={handleKeyPress}
            />
          )
        }
        break

      case FieldTypeEnum.DATE:
        if (isDateFieldConfig(config)) {
          return (
            <DateInput
              ref={inputRef}
              value={tempValue}
              onChange={handleValueChange}
              onKeyDown={handleKeyPress}
              // Enter saves like the check mark: the picker commits typed
              // text after the keydown, so confirm with the resolved value
              // directly (override-value path) instead of stale tempValue.
              onEnterConfirm={(resolvedValue) => {
                handleValueChange(resolvedValue)
                handleConfirm(resolvedValue)
              }}
              size={config.size || 'small'}
              placeholder={config.placeholder}
              format={config.format}
              showTime={config.showTime}
              allowClear={config.allowClear}
              status={validationError ? 'error' : undefined}
              autoFocus={inputMode === 'read' ? true : config.autoFocus === true}
            />
          )
        }
        break

      case FieldTypeEnum.TIME:
        if (isTimeFieldConfig(config)) {
          return (
            <TimeInput
              ref={inputRef}
              value={tempValue}
              onChange={handleValueChange}
              onKeyDown={handleKeyPress}
              size={config.size || 'small'}
              placeholder={config.placeholder}
              format={config.format}
              allowClear={config.allowClear}
              status={validationError ? 'error' : undefined}
              autoFocus={inputMode === 'read' ? true : config.autoFocus === true}
            />
          )
        }
        break

      case FieldTypeEnum.SWITCH:
        if (isSwitchFieldConfig(config)) {
          return (
            <SwitchInput
              ref={inputRef}
              value={tempValue as boolean}
              onChange={handleValueChange}
              onKeyDown={handleKeyPress}
              size={config.size || 'small'}
              disabled={config.disabled}
              checkedChildren={config.checkedChildren}
              unCheckedChildren={config.unCheckedChildren}
            />
          )
        }
        break

      case FieldTypeEnum.CHECKBOX:
        if (isCheckboxFieldConfig(config)) {
          // Auto-confirm checkboxes on change when in edit mode
          const handleCheckboxChange = (value: boolean) => {
            handleValueChange(value)
            // Auto-confirm in edit mode since checkboxes are binary and don't need manual confirmation
            // Pass value directly to ensure it's available even if state hasn't updated yet
            if (inputMode === 'edit') {
              // Use setTimeout to ensure value is updated before confirming
              setTimeout(() => {
                handleConfirm(value)
              }, 0)
            }
          }
          return (
            <CheckboxInput
              ref={inputRef}
              value={tempValue as boolean}
              onChange={handleCheckboxChange}
              onKeyDown={handleKeyPress}
              disabled={config.disabled}
              checkboxLabel={config.checkboxLabel}
            />
          )
        }
        break

      case FieldTypeEnum.DEAL:
        if (isDealFieldConfig(config)) {
          return (
            <DealSelect
              ref={inputRef}
              mode='inline'
              selectMode={config.mode || 'single'}
              value={tempValue}
              onChange={(selectedValue, option) => handleValueChange(option || selectedValue)}
              onSelect={(selectedValue, option) => handleValueChange(option || selectedValue)}
              onClear={() => handleValueChange(config.mode === 'multiple' ? [] : '')}
              size={config.size || 'small'}
              style={{ width: '100%' }}
              autoFocus={inputMode === 'read' ? true : config.autoFocus === true}
              placeholder={config.placeholder}
              onKeyDown={handleKeyPress}
              disabled={config.disabled}
            />
          )
        }
        break

      case FieldTypeEnum.PROSPECT:
        if (isProspectFieldConfig(config)) {
          return (
            <ProspectSelect
              ref={inputRef}
              mode='inline'
              selectMode={config.mode || 'single'}
              value={tempValue}
              onChange={(selectedValue, option) => handleValueChange(option || selectedValue)}
              onSelect={(selectedValue, option) => handleValueChange(option || selectedValue)}
              onClear={() => handleValueChange(config.mode === 'multiple' ? [] : '')}
              size={config.size || 'small'}
              style={{ width: '100%' }}
              autoFocus={inputMode === 'read' ? true : config.autoFocus === true}
              placeholder={config.placeholder}
              onKeyDown={handleKeyPress}
              disabled={config.disabled}
            />
          )
        }
        break

      case FieldTypeEnum.TAGS:
        if (isTagsFieldConfig(config)) {
          return (
            <TagsInput
              ref={inputRef}
              value={tempValue}
              onChange={handleValueChange}
              onKeyDown={handleKeyPress}
              size={config.size || 'small'}
              status={validationError ? 'error' : undefined}
              placeholder={config.placeholder}
              maxLength={config.maxLength}
              disabled={config.disabled}
              autoFocus={inputMode === 'read' ? true : config.autoFocus === true}
            />
          )
        }
        break

      default: {
        // Every FieldTypeEnum member has a case above, so `config` is `never`
        // here; a config whose `type` is outside the enum still renders as text.
        const fallback: BaseFieldConfig = config
        return (
          <TextInput
            ref={inputRef}
            value={tempValue}
            onChange={handleValueChange}
            onKeyDown={handleKeyPress}
            size={fallback.size || 'small'}
            status={validationError ? 'error' : undefined}
            placeholder={fallback.placeholder}
            autoFocus={inputMode === 'read' ? true : fallback.autoFocus === true}
          />
        )
      }
    }
    return null
  }

  // ============================================================================
  // Render
  // ============================================================================

  const renderEditingContent = () => {
    const isRichTextField = config.type === FieldTypeEnum.RICHTEXT

    const editingContent = (
      <div
        ref={containerRef}
        className={`inline-edit-container ${isRichTextField ? 'flex-1' : ''} ${config.className || ''}`}
        style={
          isRichTextField ? { display: 'flex', flexDirection: 'column', minHeight: 0, ...config.style } : config.style
        }
      >
        <div className={isRichTextField ? 'flex flex-col flex-1 min-h-0' : 'flex items-center gap-2'}>
          <div className={isRichTextField ? 'flex-1 min-h-0' : 'flex-1'} ref={inputRef}>
            {renderInput()}
          </div>
          {!hideActions && (
            <div className='flex items-center gap-1'>
              <Button
                type='text'
                size='small'
                className='text-green-600 hover:text-green-700 hover:bg-green-50'
                icon={<FontAwesomeIcon icon={faCheck} />}
                onClick={(e) => {
                  e.stopPropagation()
                  handleConfirm()
                }}
                loading={isSaving}
                disabled={isSaving}
                tabIndex={0}
                onKeyDown={handleKeyPress}
              />
              <Button
                type='text'
                size='small'
                className='text-red-600 hover:text-red-700 hover:bg-red-50'
                icon={<FontAwesomeIcon icon={faTimes} />}
                onClick={(e) => {
                  e.stopPropagation()
                  handleCancel()
                }}
                disabled={isSaving}
                tabIndex={0}
                onKeyDown={handleKeyPress}
              />
            </div>
          )}
        </div>
        {validationError && <div className='text-red-500 text-xs mt-1'>{validationError}</div>}
      </div>
    )

    // Wrap with Popconfirm if changes need confirmation
    if (showPopconfirm) {
      return (
        <Popconfirm
          title='You have unsaved changes'
          description='Do you want to save your changes?'
          onConfirm={handlePopconfirmConfirm}
          onCancel={handlePopconfirmCancel}
          okText='Save'
          cancelText='Discard'
          placement='topLeft'
          open={showPopconfirm}
        >
          {editingContent}
        </Popconfirm>
      )
    }

    return editingContent
  }

  const handleStartEditClick = () => {
    // For TEXT_ACTION and LINK fields, call onClick instead of starting edit mode
    if ((config.type === FieldTypeEnum.TEXT_ACTION || config.type === FieldTypeEnum.LINK) && onClick) {
      onClick(config.key, config.value, record)
      return
    }

    handleStartEdit()
  }

  if (isEditing) {
    return renderEditingContent()
  }

  return (
    <div
      className={`inline-edit-label ${isReadonly ? 'cursor-default' : 'cursor-pointer'} ${isReadonly ? '' : 'hover:bg-gray-50'} px-1 py-0 rounded ${
        config.className || ''
      } ${alignment === 'right' ? 'text-right' : alignment === 'center' ? 'text-center' : 'text-left'}`}
      style={{
        minHeight: '20px',
        textAlign: alignment === 'right' ? 'right' : alignment === 'center' ? 'center' : 'left',
        width: '100%',
        display: 'flex',
        justifyContent: alignment === 'right' ? 'flex-end' : alignment === 'center' ? 'center' : 'flex-start',
        ...config.style
      }}
      onClick={handleStartEditClick}
    >
      {typeof displayValue === 'string' ? (
        <Text
          className={`${displayValue ? '' : 'text-gray-400'} ${
            (config.type === FieldTypeEnum.TEXT_ACTION || config.type === FieldTypeEnum.LINK) && !isReadonly
              ? 'cursor-pointer text-blue-600 underline font-medium'
              : config.type === FieldTypeEnum.TEXT_ACTION || config.type === FieldTypeEnum.LINK
                ? 'text-blue-600 underline font-medium cursor-pointer'
                : ''
          } ${textClassName || ''}`}
          data-alignment={alignment}
          style={{ minWidth: 0, flexShrink: 1 }}
        >
          {displayValue || ''}
        </Text>
      ) : (
        <div
          className={`${displayValue ? '' : 'text-gray-400'} ${
            (config.type === FieldTypeEnum.TEXT_ACTION || config.type === FieldTypeEnum.LINK) && !isReadonly
              ? 'cursor-pointer text-blue-600 underline font-medium'
              : config.type === FieldTypeEnum.TEXT_ACTION || config.type === FieldTypeEnum.LINK
                ? 'text-blue-600 underline font-medium cursor-pointer'
                : ''
          } ${textClassName || ''}`}
          data-alignment={alignment}
          style={{ minWidth: 0, flexShrink: 1 }}
        >
          {displayValue || ''}
        </div>
      )}
    </div>
  )
}
